import React, {
  InputHTMLAttributes,
  ReactNode,
  memo,
  useRef, useEffect, useState,
} from 'react';
import cx from 'classnames';

import { excludedKeys } from './constants';
import s from './BInput.module.scss';

export interface BInputProps extends InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
  disabled?: boolean;
  errorMessage?: string;
  fullWidth?: boolean;
}

export const BInput = memo<BInputProps>((props: BInputProps) => {
  const {
    className,
    fullWidth,
    disabled,
    value,
    style,
    errorMessage,
    onChange,
    endAdornment,
    startAdornment,
    name,
    ...otherProps
  } = props;

  const inputRef = useRef<any>();
  const [inputUsingMouse, setInputUsingMouse] = useState(false);

  const modsLabel = {
    [s.fullWidth]: fullWidth,
  };

  const modsInput = {
    [s.isErrorMessage]: Boolean(errorMessage),
    [s.isStartAdornment]: Boolean(startAdornment),
    [s.isEndAdornment]: Boolean(endAdornment),
    [s.isEntered]: Boolean(value),
  };

  const modsClear = {
    [s.isEndAdornment]: Boolean(endAdornment),
  };

  const handleClear = () => {
    if (!inputRef?.current || !onChange) {
      return;
    }

    onChange({ ...inputRef.current, target: { ...inputRef.current.target, value: '', name } });
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        setInputUsingMouse(false);
        return;
      }

      if (excludedKeys.includes(event.key)) {
        return;
      }

      setInputUsingMouse(true);
    };

    const onMouseDownOrBlur = () => {
      setInputUsingMouse(true);
    };

    const onBlur = () => {
      setInputUsingMouse(false);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onMouseDownOrBlur);
    window.addEventListener('blur', onBlur);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onMouseDownOrBlur);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  return (
    <div
      className={cx(s.feInput, modsLabel, className, 'clear-input')}
      style={style}
    >
      {startAdornment && <span className={s.startAdornment}>{startAdornment}</span>}
      <input
        ref={inputRef}
        name={name}
        disabled={disabled}
        value={value}
        onChange={onChange}
        className={cx(s.input, modsInput, { [s.usingMouse]: inputUsingMouse })}
        {...otherProps}
      />
      {value && (
        <span
          className={cx(s.clearIcon, modsClear)}
          onClick={handleClear}
        />
      )}
      {endAdornment && <span className={s.endAdornment}>{endAdornment}</span>}
      {errorMessage && <span className={s.messageError}>{errorMessage}</span>}
    </div>
  );
});
